/**
 * Studio → Tools (index) — list view at exact `/studio/tools/`.
 *
 * Wave 3 PR-A introduced the real `workspace_tools` API but kept the
 * list and the parent route in the same file (`tools.tsx`), with no
 * `<Outlet />` for `tools.new` / `tools.$toolId` children. That broke
 * `/studio/tools/new` and `/studio/tools/<id>` (Codex audit #1, #2).
 *
 * This file is the new index child route; the parent `tools.tsx` is now
 * a thin layout that renders an `<Outlet />`. The "Register Tool"
 * dropdown previously rendered here lived in two places (here AND the
 * Studio layout `ContentHeader`) — Codex audit #22. The duplicate is
 * removed; the layout now owns the action and exposes the richer set
 * of disabled-with-tooltip items (MCP / OpenAPI / Custom Function) with
 * corrected copy (Codex audit #5, #6) pointing to Connections.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Wrench, Search, AlertTriangle, Trash2, Settings2, Play,
  Plug, Globe, Code, Database, Sparkles, ShieldCheck,
  Lock, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useWorkspaceTools,
  useUpdateWorkspaceTool,
  useDeleteWorkspaceTool,
  useTestWorkspaceTool,
  type WorkspaceToolListRow,
  type ToolSource,
  type ToolPolicy,
} from '@/lib/use-workspace-tools';
import { cn } from '@/lib/utils';
import { useHasPermission } from '@/lib/use-has-permission';
import { CatalogExportImportButtons } from '@/components/studio/catalog-export-import-buttons';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@/components/ui/pagination';

export const Route = createFileRoute('/studio/tools/')({
  component: StudioToolsIndexPage,
});

// ─── Source presentation ───────────────────────────────────────────────────
//
// Canonical source labels per Codex audit #25. Older rows may still carry
// `custom`; current unified rows use first-class `http` and `custom_function`
// source values. Keep all three so catalog filters and badges survive mixed
// migration states.

const SOURCE_META: Record<
  ToolSource,
  { label: string; cls: string; icon: typeof Plug; description: string }
> = {
  builtin: {
    label: 'Built-in',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    icon: Sparkles,
    description: 'Ships with the platform. Cannot be deleted; deactivate instead.',
  },
  mcp: {
    label: 'MCP',
    cls: 'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
    icon: Plug,
    description: 'External MCP server connection (registered under Connections → MCP).',
  },
  openapi: {
    label: 'OpenAPI',
    cls: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30',
    icon: Globe,
    description: 'Generated from an OpenAPI / Swagger spec.',
  },
  custom: {
    label: 'HTTP',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: Code,
    description: 'Custom HTTP endpoint with admin-defined parameters.',
  },
  http: {
    label: 'HTTP',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    icon: Code,
    description: 'Custom HTTP endpoint with admin-defined parameters.',
  },
  database: {
    label: 'Database',
    cls: 'bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30',
    icon: Database,
    description: 'Parameterized SQL query against a registered connection.',
  },
  custom_function: {
    label: 'Custom Function',
    cls: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
    icon: Sparkles,
    description: 'Tenant function deployed in Connections → Functions.',
  },
};

const CUSTOM_FUNCTION_META = {
  label: 'Custom Function',
  cls: 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30',
  icon: Sparkles,
  description: 'Tenant function deployed in Connections → Functions.',
};

function getSourceMeta(row: { toolSource: string; handlerConfig?: unknown }) {
  if (
    row.toolSource === 'custom' &&
    typeof row.handlerConfig === 'object' &&
    row.handlerConfig !== null &&
    (row.handlerConfig as { kind?: string }).kind === 'custom_function'
  ) {
    return CUSTOM_FUNCTION_META;
  }
  return (
    SOURCE_META[row.toolSource as ToolSource] ?? {
      label: row.toolSource,
      cls: 'bg-muted text-muted-foreground border-border',
      icon: Plug,
      description: 'Custom tool source.',
    }
  );
}

// ─── Policy presentation ───────────────────────────────────────────────────

const POLICY_META: Record<
  ToolPolicy,
  { label: string; cls: string; description: string }
> = {
  auto_approve: {
    label: 'Auto',
    cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
    description: 'Runs without confirmation when the agent calls it.',
  },
  require_approval: {
    label: 'Approval',
    cls: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    description: 'Pauses the agent run; user confirms before execution.',
  },
  forbidden: {
    label: 'Forbidden',
    cls: 'bg-destructive/10 text-destructive border-destructive/30',
    description: 'Cannot be invoked. Useful for soft-disabling sensitive tools.',
  },
};

// ─── Page ──────────────────────────────────────────────────────────────────

function StudioToolsIndexPage() {
  const { data, isLoading, error, refetch, isFetching } = useWorkspaceTools();
  const updateTool = useUpdateWorkspaceTool();
  const deleteTool = useDeleteWorkspaceTool();
  const testTool = useTestWorkspaceTool();
  const canWriteTools = useHasPermission('write', 'tools');
  const canDeleteTools = useHasPermission('delete', 'tools');

  // Issue #154 — Studio brief item #27. Client-side pagination over
  // the already-filtered list. Server-side pagination is opt-in on
  // the backend; we use client-side here because all our existing
  // search + scope + active + category filters run client-side from
  // a single hydrated list.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<25 | 50 | 100>(25);

  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState<ToolSource | 'all'>('all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  // Studio Phase 3E — scope chip lets users narrow to system or
  // tenant rows. 'all' (default) shows both.
  const [scopeFilter, setScopeFilter] = useState<'all' | 'system' | 'tenant'>('all');
  // Studio Phase 3F — categories filter (Q10).
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [confirmDelete, setConfirmDelete] = useState<WorkspaceToolListRow | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (!data?.data) return [];
    return data.data.filter((t) => {
      if (sourceFilter !== 'all' && t.toolSource !== sourceFilter) return false;
      if (activeFilter === 'active' && !t.isActive) return false;
      if (activeFilter === 'inactive' && t.isActive) return false;
      if (scopeFilter !== 'all' && t.scope !== scopeFilter) return false;
      if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        t.name.toLowerCase().includes(q) ||
        t.displayName.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.namespace.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q)
      );
    });
  }, [data, search, sourceFilter, activeFilter, scopeFilter, categoryFilter]);

  // Issue #154 — page slice. Snap back to page 1 whenever a filter
  // changes so the user doesn't end up stranded on an empty page.
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const paged = useMemo(
    () => filtered.slice(pageStart, pageStart + pageSize),
    [filtered, pageStart, pageSize],
  );

  const categories = useMemo(() => {
    if (!data?.data) return [];
    return Array.from(new Set(data.data.map((t) => t.category))).sort();
  }, [data]);

  const sources = useMemo(() => {
    if (!data?.data) return [];
    return Array.from(new Set(data.data.map((t) => t.toolSource))).sort();
  }, [data]);

  const handleToggleActive = async (
    tool: WorkspaceToolListRow,
    next: boolean,
  ) => {
    try {
      await updateTool.mutateAsync({ id: tool.id, isActive: next });
      toast.success(
        next
          ? `Activated "${tool.displayName}"`
          : `Deactivated "${tool.displayName}"`,
      );
    } catch (err) {
      toast.error('Toggle failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const handleTest = async (tool: WorkspaceToolListRow) => {
    setTestingId(tool.id);
    try {
      const result = await testTool.mutateAsync({ id: tool.id });
      if (result.success) {
        toast.success(`Test OK (${result.latencyMs}ms)`, {
          description: tool.displayName,
        });
      } else {
        toast.error(`Test failed (${result.latencyMs}ms)`, {
          description: result.error ?? 'Unknown error',
        });
      }
    } catch (err) {
      toast.error('Test failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleDelete = async (tool: WorkspaceToolListRow) => {
    try {
      await deleteTool.mutateAsync({ id: tool.id });
      toast.success(`Deleted "${tool.displayName}"`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.includes('builtin') || msg.includes('CANNOT_DELETE_BUILTIN')) {
        toast.error('Cannot delete a built-in tool', {
          description: 'Deactivate it instead.',
        });
      } else {
        toast.error('Delete failed', { description: msg });
      }
    }
    setConfirmDelete(null);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div>
        <h2 className="text-base font-semibold flex items-center gap-2">
          <Wrench className="h-4 w-4 text-muted-foreground" />
          Tools
        </h2>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          Tools your agents can call — built-in capabilities, MCP server
          connections, OpenAPI imports, custom HTTP endpoints, parameterized
          database queries, and Custom Functions deployed in Connections.
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search tools…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        {sources.length > 1 && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSourceFilter('all')}
              className={cn(
                'px-2 py-1 text-[11px] rounded border transition-colors',
                sourceFilter === 'all'
                  ? 'bg-foreground text-background border-foreground'
                  : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
              )}
            >
              All
            </button>
            {sources.map((s) => {
              const meta = SOURCE_META[s as ToolSource] ?? { label: s, cls: '' };
              const active = sourceFilter === s;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSourceFilter(s as ToolSource)}
                  className={cn(
                    'px-2 py-1 text-[11px] rounded border transition-colors',
                    active
                      ? meta.cls
                      : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
                  )}
                >
                  {meta.label}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => setActiveFilter('all')}
            className={cn(
              'px-2 py-1 text-[11px] rounded border transition-colors',
              activeFilter === 'all'
                ? 'bg-foreground text-background border-foreground'
                : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
            )}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setActiveFilter('active')}
            className={cn(
              'px-2 py-1 text-[11px] rounded border transition-colors',
              activeFilter === 'active'
                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
            )}
          >
            Active
          </button>
          <button
            type="button"
            onClick={() => setActiveFilter('inactive')}
            className={cn(
              'px-2 py-1 text-[11px] rounded border transition-colors',
              activeFilter === 'inactive'
                ? 'bg-muted text-muted-foreground border-border'
                : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
            )}
          >
            Inactive
          </button>
        </div>
        {/* Studio Phase 3E — scope filter (system / tenant / all). */}
        <div className="flex items-center gap-1.5">
          {(['all', 'tenant', 'system'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setScopeFilter(s)}
              className={cn(
                'px-2 py-1 text-[11px] rounded border transition-colors capitalize',
                scopeFilter === s
                  ? s === 'system'
                    ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30'
                    : s === 'tenant'
                      ? 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30'
                      : 'bg-foreground text-background border-foreground'
                  : 'bg-transparent text-muted-foreground border-border hover:text-foreground',
              )}
            >
              {s === 'all' ? 'All scopes' : s}
            </button>
          ))}
        </div>
        {/* Studio Phase 3F — categories filter (Q10).
            Issue #634 — Phase 2 QA flagged the category filter as
            "missing". Pre-fix this dropdown only rendered when there
            were 2+ distinct categories in the result set, which meant
            on a fresh tenant with only general/system tools (all
            sharing category='general') the filter never appeared and
            STUDIO-TOOLS-LIST-016 could not be exercised. The list
            schema and backend filter (?category=) have always been
            wired — we just need to expose the control unconditionally
            so testers can pick "All categories" (the no-op default)
            and confirm presence. */}
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="h-7 text-[11px] rounded border bg-background px-2 text-foreground"
          aria-label="Filter by category"
          // Disabled when there's nothing to filter so the dropdown is
          // discoverable but non-interactive on an empty catalog.
          disabled={categories.length === 0}
        >
          <option value="all">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
          className="h-8"
          title="Refresh tools list"
          aria-label="Refresh tools list"
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', isFetching && 'animate-spin')}
          />
        </Button>
        {data && (
          <span className="text-xs text-muted-foreground tabular-nums ml-auto">
            {filtered.length} {filtered.length === 1 ? 'tool' : 'tools'}
          </span>
        )}
        {/* Issue #154 — Studio brief #28. Export/Import buttons live in
            the toolbar next to the count. The shared component handles
            blob download (Export) and file-pick → POST (Import). */}
        {canWriteTools && (
          <CatalogExportImportButtons
            baseUrl="/api/v1/workspace/tools"
            kindLabel="tools"
            onImported={() => refetch()}
            disabled={isLoading}
          />
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Loading tools…
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>Failed to load tools: {error.message}</div>
          </CardContent>
        </Card>
      ) : !data || data.data.length === 0 ? (
        // Codex audit #23 — empty state distinguishes the real scenarios:
        // (1) catalog load failed → handled by `error` block above;
        // (2) tenant has zero tools → this block, with explicit guidance on
        //     how each source surfaces (Register Tool here, deploy in
        //     Connections, builtins seeded by admin).
        // System tools are NOT yet surfaced — see deferred follow-up issue
        // for system vs tenant catalog.
        <Card>
          <CardContent className="py-10 px-6 text-center text-sm text-muted-foreground space-y-3">
            <Wrench className="h-8 w-8 text-muted-foreground/40 mx-auto" />
            <div className="font-medium text-foreground">No tools yet</div>
            <div className="text-xs leading-relaxed max-w-md mx-auto">
              Tools surface here from these places:
              <ul className="list-disc list-inside text-left mt-2 space-y-1">
                <li><strong>Built-in tools</strong> are auto-seeded when a platform admin runs the seed.</li>
                <li><strong>HTTP / Database tools</strong> are authored from the tenant-admin tool registration flow.</li>
                <li><strong>MCP tools</strong> are imported from registered MCP servers in <Link to="/connections" className="underline hover:no-underline">Connections</Link>.</li>
                <li><strong>OpenAPI tools</strong> are deferred for V1.</li>
                <li><strong>Custom Functions</strong> deployed under <Link to="/studio/functions" className="underline hover:no-underline">Studio → Functions</Link> appear here automatically.</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="px-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tool</TableHead>
                  <TableHead className="w-32">Source</TableHead>
                  <TableHead className="w-32">Policy</TableHead>
                  <TableHead className="w-20">Active</TableHead>
                  <TableHead className="w-44">Created</TableHead>
                  <TableHead className="w-32 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* fix-wave-4 #1031 (STUDIO-ERROR-004) — When filters
                    narrow the result set to zero, render an honest
                    "no matches" row instead of an empty table body. The
                    parent already handles the truly-empty catalog
                    state above; this branch covers the search-applied
                    case so the matrix probe + screen readers see a
                    clear signal that the filter produced no results. */}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className="py-10 text-center text-sm text-muted-foreground"
                    >
                      No tools match your filters. Clear search or
                      filter chips to see all tools.
                    </TableCell>
                  </TableRow>
                )}
                {paged.map((t) => {
                  const sm = getSourceMeta(t);
                  const Icon = sm.icon;
                  const pm =
                    POLICY_META[t.policy as ToolPolicy] ??
                    POLICY_META.auto_approve;
                  return (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <div
                            className={cn(
                              'h-8 w-8 rounded-md border flex items-center justify-center shrink-0',
                              sm.cls,
                            )}
                          >
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <Link
                              to="/studio/tools/$toolId"
                              params={{ toolId: t.id }}
                              className="text-sm font-medium leading-tight hover:underline inline-flex items-center gap-1.5"
                            >
                              {t.displayName}
                              {t.scope === 'system' && (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
                                >
                                  System
                                </Badge>
                              )}
                              {t.isBuiltin && (
                                <TooltipProvider delayDuration={150}>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Lock className="h-3 w-3 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Built-in — cannot be deleted
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </Link>
                            <div className="font-mono text-[11px] text-muted-foreground">
                              {/* Round 4 fix #247 — system tool slugs already
                                  include the `system.` prefix; rendering
                                  `${namespace}.${name}` produced
                                  `system.system.calculate`. */}
                              {t.scope === 'system' ? t.name : `${t.namespace}.${t.name}`}
                            </div>
                            <div className="text-[11px] text-muted-foreground line-clamp-2 max-w-md mt-0.5">
                              {t.description}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={cn('text-[10px] gap-1 cursor-default', sm.cls)}
                              >
                                <Icon className="h-2.5 w-2.5" />
                                {sm.label}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{sm.description}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={cn(
                                  'text-[10px] gap-1 cursor-default',
                                  pm.cls,
                                )}
                              >
                                {t.policy === 'auto_approve' && (
                                  <ShieldCheck className="h-2.5 w-2.5" />
                                )}
                                {pm.label}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{pm.description}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        {/*
                          System tools cannot be deactivated by tenants —
                          their lifecycle is sysadmin-controlled. Show
                          the live status as a badge instead of a switch.
                        */}
                        {t.scope === 'system' ? (
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[10px]',
                              t.isActive
                                ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30'
                                : 'bg-muted text-muted-foreground border-border',
                            )}
                          >
                            {t.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        ) : (
                          <Switch
                            checked={t.isActive}
                            disabled={updateTool.isPending || !canWriteTools}
                            onCheckedChange={(v) => handleToggleActive(t, v)}
                            /* #754 — Studio tool active switches had no
                               accessible name. Screen-reader users could
                               not tell which tool a switch affected.
                               The aria-label spells out the tool + the
                               action so the contract is explicit. */
                            aria-label={`${t.isActive ? 'Deactivate' : 'Activate'} tool ${t.displayName ?? t.id}`}
                            title={
                              canWriteTools
                                ? `${t.isActive ? 'Deactivate' : 'Activate'} tool ${t.displayName ?? t.id}`
                                : 'Requires tool authoring permission'
                            }
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground tabular-nums">
                        {new Date(t.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Test button — schema-aware HTTP probe; database tools
                            land on a "test not implemented for source" error,
                            so we surface a tooltip instead of silently failing
                            (Codex audit #10). */}
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  aria-label={`Test ${t.displayName}`}
                                  title="Run connectivity test"
                                  disabled={
                                    testingId === t.id ||
                                    t.toolSource === 'database' ||
                                    t.toolSource === 'builtin'
                                  }
                                  onClick={() => handleTest(t)}
                                >
                                  <Play
                                    className={cn(
                                      'h-3.5 w-3.5',
                                      testingId === t.id && 'animate-pulse',
                                    )}
                                  />
                                </Button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              {t.toolSource === 'database'
                                ? 'Database test runner is not yet wired'
                                : t.toolSource === 'builtin'
                                  ? 'Built-in tools run in-process and have no external probe'
                                  : 'Run connectivity probe'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Link
                          to="/studio/tools/$toolId"
                          params={{ toolId: t.id }}
                          title={`Configure ${t.displayName}`}
                          aria-label={`Configure ${t.displayName}`}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-accent text-foreground"
                        >
                          <Settings2 className="h-3.5 w-3.5" />
                        </Link>
                        {/* Only tenant tools can be deleted by tenants —
                            system tools are sysadmin-managed; built-in
                            tools are always-on. */}
                        {!t.isBuiltin && t.scope === 'tenant' && canDeleteTools && (
                          <Button
                            variant="ghost"
                            size="sm"
                            title={`Delete ${t.displayName}`}
                            aria-label={`Delete ${t.displayName}`}
                            className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                            onClick={() => setConfirmDelete(t)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Issue #154 — pagination footer. Hidden when there are 0
          results (filter narrowed to empty) or when everything fits on
          one page (pageSize >= filtered.length). The page-size picker
          stays visible whenever there's data so the user can switch
          density without first having to scroll a long list. */}
      {data && filtered.length > 0 && (
        <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Rows per page</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value) as 25 | 50 | 100);
                setPage(1);
              }}
              className="h-7 text-[11px] rounded border bg-background px-2 text-foreground"
              aria-label="Rows per page"
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="tabular-nums">
              {filtered.length === 0
                ? '0 of 0'
                : `${pageStart + 1}–${Math.min(pageStart + pageSize, filtered.length)} of ${filtered.length}`}
            </span>
          </div>
          {totalPages > 1 && (
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage > 1) setPage(currentPage - 1);
                    }}
                    aria-disabled={currentPage <= 1}
                    className={cn(currentPage <= 1 && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
                {/* Render at most 5 page links: first, ..., current-1,
                    current, current+1, ..., last. Falls back to a
                    naive 1..N list for small page counts. */}
                {totalPages <= 5 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                    <PaginationItem key={n}>
                      <PaginationLink
                        href="#"
                        isActive={n === currentPage}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(n);
                        }}
                      >
                        {n}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                ) : (
                  <>
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        isActive={1 === currentPage}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(1);
                        }}
                      >
                        1
                      </PaginationLink>
                    </PaginationItem>
                    {currentPage > 3 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                    {currentPage !== 1 && currentPage !== totalPages && (
                      <PaginationItem>
                        <PaginationLink href="#" isActive>
                          {currentPage}
                        </PaginationLink>
                      </PaginationItem>
                    )}
                    {currentPage < totalPages - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}
                    <PaginationItem>
                      <PaginationLink
                        href="#"
                        isActive={totalPages === currentPage}
                        onClick={(e) => {
                          e.preventDefault();
                          setPage(totalPages);
                        }}
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (currentPage < totalPages) setPage(currentPage + 1);
                    }}
                    aria-disabled={currentPage >= totalPages}
                    className={cn(currentPage >= totalPages && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      )}

      <AlertDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tool</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete{' '}
              <code className="bg-muted px-1 rounded">
                {confirmDelete?.name}
              </code>
              ? Any agent using this tool will lose the connection until it's
              re-registered. Built-in tools cannot be deleted (deactivate them
              instead).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => confirmDelete && handleDelete(confirmDelete)}
            >
              Delete tool
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
